import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ListChecks, Search, SearchX } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { AppButton, AppModal, Callout, Checkbox, EmptyState, Input, NativeSelect } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import { DIFFICULTY_LABELS, QUESTION_TYPE_LABELS } from "./builderRules";

const PAGE_SIZE = 40;
const SELECT_ALL_CAP = 2000;

const rowOf = (q) => ({
  question: String(q._id),
  questionText: q.questionText || "Question",
  questionType: q.questionType || "",
  difficulty: q.difficulty || "",
  marks: q.marks ?? 1,
  overrideMarks: null,
});

const metaLine = (q) => [
  QUESTION_TYPE_LABELS[q.questionType] || q.questionType,
  DIFFICULTY_LABELS[q.difficulty],
  `${q.marks ?? 1} ${(q.marks ?? 1) === 1 ? "mark" : "marks"}`,
  [q.category?.name, q.subcategory?.name].filter(Boolean).join(" › "),
].filter(Boolean).join(" · ");

/**
 * Pick questions from the question bank for one section.
 *
 * Opens with the section's own questions ticked. A question already in another
 * section is shown but cannot be ticked — one question twice on a paper would
 * share one answer. Confirming keeps the order of the questions already chosen
 * and adds the new ones after them.
 */
export default function QuestionPickerDialog({
  open, onClose, onConfirm, sectionName, current = [], elsewhere, categories = [],
}) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [type, setType] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState("idle"); // idle | loading | more | error
  const [picked, setPicked] = useState(() => new Map());
  const [bulk, setBulk] = useState(false);
  const [note, setNote] = useState("");
  const requestRef = useRef(0);

  // A fresh session every time the dialog opens, starting from the section.
  useEffect(() => {
    if (!open) return;
    setPicked(new Map(current.map((q) => [String(q.question), q])));
    setNote("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p = {};
    if (debounced) p.search = debounced;
    if (category) p.category = category;
    if (subcategory) p.subcategory = subcategory;
    if (difficulty) p.difficulty = difficulty;
    if (type) p.questionType = type;
    return p;
  }, [debounced, category, subcategory, difficulty, type]);

  const load = useCallback(async (nextPage) => {
    const req = ++requestRef.current;
    setState(nextPage === 1 ? "loading" : "more");
    try {
      const { data } = await apiClient.get("/assessments/questions-for-selection", {
        params: { ...params, page: nextPage, limit: PAGE_SIZE },
      });
      if (req !== requestRef.current) return;
      setRows((prev) => (nextPage === 1 ? data.questions || [] : [...prev, ...(data.questions || [])]));
      setTotal(data.total ?? 0);
      setPage(nextPage);
      setState("idle");
    } catch {
      if (req === requestRef.current) setState("error");
    }
  }, [params]);

  useEffect(() => {
    if (open) load(1);
  }, [open, load]);

  const topCategories = useMemo(() => categories.filter((c) => !c.parentCategory), [categories]);
  const subtopics = useMemo(
    () => categories.filter((c) => String(c.parentCategory?._id || c.parentCategory || "") === category && category),
    [categories, category]
  );

  const usedElsewhere = (id) => elsewhere?.get(String(id));

  const toggle = (q) => {
    const id = String(q._id);
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, rowOf(q));
      return next;
    });
  };

  const selectAll = async () => {
    setBulk(true);
    setNote("");
    try {
      const { data } = await apiClient.get("/assessments/questions-for-selection", {
        params: { ...params, page: 1, limit: SELECT_ALL_CAP },
      });
      const all = data.questions || [];
      let added = 0;
      let skipped = 0;
      const next = new Map(picked);
      for (const q of all) {
        const id = String(q._id);
        if (next.has(id)) continue;
        if (usedElsewhere(id)) { skipped += 1; continue; }
        next.set(id, rowOf(q));
        added += 1;
      }
      setPicked(next);
      const beyond = (data.total ?? all.length) - all.length;
      setNote([
        added ? `Added ${added} question${added === 1 ? "" : "s"}.` : "Every matching question is already ticked.",
        skipped ? `${skipped} already in another section ${skipped === 1 ? "was" : "were"} left out.` : "",
        beyond > 0 ? `${beyond} more match — narrow the filters to reach them.` : "",
      ].filter(Boolean).join(" "));
    } catch {
      setNote("Could not select them all. Try again.");
    } finally {
      setBulk(false);
    }
  };

  const confirm = () => {
    const kept = current.filter((q) => picked.has(String(q.question)));
    const keptIds = new Set(kept.map((q) => String(q.question)));
    const added = [...picked.values()].filter((q) => !keptIds.has(String(q.question)));
    onConfirm([...kept, ...added]);
  };

  const filtersOn = Boolean(debounced || category || subcategory || difficulty || type);
  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setSubcategory("");
    setDifficulty("");
    setType("");
  };

  return (
    <AppModal
      show={open}
      onHide={onClose}
      size="xl"
      title={`Add questions to ${sectionName}`}
      footer={(
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
          <span className="tw-text-[0.8125rem] tw-text-content-muted" role="status" aria-live="polite">
            {picked.size} selected
          </span>
          <div className="tw-flex tw-gap-2">
            <AppButton type="button" variant="outline" onClick={onClose}>Cancel</AppButton>
            <AppButton type="button" onClick={confirm}>Use {picked.size} question{picked.size === 1 ? "" : "s"}</AppButton>
          </div>
        </div>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-grid tw-gap-2 min-[768px]:tw-grid-cols-2 min-[1200px]:tw-grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))]">
          <Input
            type="search"
            aria-label="Search questions"
            placeholder="Search question text or tags"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leading={<Search size={16} />}
            autoFocus
          />
          <NativeSelect
            aria-label="Category"
            value={category}
            onChange={(e) => { setCategory(e.target.value); setSubcategory(""); }}
          >
            <option value="">All categories</option>
            {topCategories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </NativeSelect>
          <NativeSelect
            aria-label="Subtopic"
            value={subcategory}
            onChange={(e) => setSubcategory(e.target.value)}
            disabled={!category || subtopics.length === 0}
          >
            <option value="">{category && subtopics.length === 0 ? "No subtopics" : "All subtopics"}</option>
            {subtopics.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </NativeSelect>
          <NativeSelect aria-label="Difficulty" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
            <option value="">All levels</option>
            {Object.entries(DIFFICULTY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </NativeSelect>
          <NativeSelect aria-label="Question type" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {Object.entries(QUESTION_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </NativeSelect>
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-[0.8125rem] tw-text-content-muted">
            {state === "loading" ? "Searching…" : `${total.toLocaleString("en-IN")} question${total === 1 ? "" : "s"} ${filtersOn ? "match" : "in your bank"}`}
          </span>
          <AppButton type="button" size="sm" variant="outline" onClick={selectAll} disabled={bulk || total === 0}>
            <ListChecks size={16} aria-hidden="true" />
            {bulk ? "Selecting…" : `Select all ${total > SELECT_ALL_CAP ? SELECT_ALL_CAP.toLocaleString("en-IN") : total.toLocaleString("en-IN")}`}
          </AppButton>
          {picked.size > 0 && (
            <AppButton type="button" size="sm" variant="ghost" onClick={() => setPicked(new Map())}>Clear selection</AppButton>
          )}
          {filtersOn && (
            <AppButton type="button" size="sm" variant="ghost" onClick={clearFilters}>Clear filters</AppButton>
          )}
        </div>
        {note && <Callout tone="info" role="status">{note}</Callout>}

        {state === "error" && (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={() => load(1)}>Try again</AppButton>}>
            The question bank could not be loaded.
          </Callout>
        )}

        {state === "loading" ? (
          <Shimmer height={52} count={6} />
        ) : rows.length === 0 && state !== "error" ? (
          <EmptyState
            icon={SearchX}
            title={filtersOn ? "No questions match" : "Your question bank is empty"}
            description={filtersOn
              ? "Try another word, or clear the filters."
              : "Add questions in Question Bank first — or upload them from Excel — then come back."}
          />
        ) : (
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-0">
            {rows.map((q) => {
              const id = String(q._id);
              const other = usedElsewhere(id);
              return (
                <li key={id} className="tw-px-3 tw-py-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
                  <Checkbox
                    checked={picked.has(id)}
                    onChange={() => toggle(q)}
                    disabled={!!other}
                    label={q.questionText || "Question"}
                    labelClassName="tw-line-clamp-2"
                    description={other ? `Already in ${other.name}` : metaLine(q)}
                    className="tw-w-full"
                  />
                </li>
              );
            })}
          </ul>
        )}

        {rows.length > 0 && rows.length < total && (
          <div className="tw-flex tw-justify-center">
            <AppButton type="button" variant="outline" size="sm" onClick={() => load(page + 1)} disabled={state === "more"}>
              {state === "more" ? "Loading…" : `Show more (${(total - rows.length).toLocaleString("en-IN")} left)`}
            </AppButton>
          </div>
        )}
      </div>
    </AppModal>
  );
}
